const usdFull = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
})

const usdWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

export function usd(value: number, opts: { compact?: boolean; whole?: boolean } = {}): string {
  if (!Number.isFinite(value)) return '-'
  if (opts.compact && Math.abs(value) >= 10_000) return usdCompact.format(value)
  if (opts.whole && Number.isInteger(value)) return usdWhole.format(value)
  // Sub-cent values round to "$0.00", which reads as a bug rather than as a
  // very small number, so they keep significant digits instead.
  if (value !== 0 && Math.abs(value) < 0.01) return `$${value.toPrecision(2)}`
  return usdFull.format(value)
}

export const cents = (value: number, opts?: { compact?: boolean; whole?: boolean }) =>
  usd(value / 100, opts)

export function multiple(value: number): string {
  if (!Number.isFinite(value)) return '-'
  if (value >= 100) return `${Math.round(value)}×`
  if (value >= 10) return `${value.toFixed(1)}×`
  return `${value.toFixed(2)}×`
}

export function percent(value: number, places = 2): string {
  if (!Number.isFinite(value)) return '-'
  // A 1-in-6,667 chance printed as "0.00%" tells the reader nothing, so very
  // long odds are shown as a ratio instead.
  if (value > 0 && value < 0.0005) return `1 in ${Math.round(1 / value).toLocaleString()}`
  return `${(value * 100).toFixed(places)}%`
}

/** Token quantities span 10^-6 to 10^9, so no single fixed precision works. */
export function quantity(value: number): string {
  if (!Number.isFinite(value)) return '-'
  if (value >= 1_000_000) return value.toLocaleString('en-US', { maximumFractionDigits: 0 })
  if (value >= 1) return value.toLocaleString('en-US', { maximumFractionDigits: 4 })
  if (value >= 0.0001) return value.toFixed(6)
  return value.toExponential(3)
}

export function compactNumber(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

export function shortHash(hash: string, length = 8): string {
  return hash.length <= length * 2 ? hash : `${hash.slice(0, length)}…${hash.slice(-4)}`
}

export function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`
}

export function timeAgo(input: string | number): string {
  const then = typeof input === 'number' ? input : Date.parse(input)
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))

  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export function dateOnly(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
